import React, { useState, useEffect } from 'react';
import { X, Save, Pill } from 'lucide-react';

const MedicamentEditModal = ({ medicament, isOpen, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    genericName: '',
    category: 'Autre',
    dosageForm: '',
    strength: '',
    unit: 'mg',
    manufacturer: '',
    description: '',
    stockQuantity: 0,
    minStockLevel: 10,
    maxStockLevel: 100,
    price: '',
    requiresPrescription: true,
    barcode: ''
  });

  useEffect(() => {
    if (medicament) {
      setFormData({
        name: medicament.name || '',
        genericName: medicament.genericName || '',
        category: medicament.category || 'Autre',
        dosageForm: medicament.dosageForm || '',
        strength: medicament.strength || '',
        unit: medicament.unit || 'mg',
        manufacturer: medicament.manufacturer || '',
        description: medicament.description || '',
        stockQuantity: medicament.stockQuantity || 0,
        minStockLevel: medicament.minStockLevel || 10,
        maxStockLevel: medicament.maxStockLevel || 100,
        price: medicament.price || '',
        requiresPrescription: medicament.requiresPrescription !== false,
        barcode: medicament.barcode || ''
      });
    }
  }, [medicament]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(medicament.id, formData);
  };

  if (!isOpen || !medicament) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <Pill className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Modifier le Médicament</h2>
              <p className="text-gray-600">Mettre à jour les informations</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" disabled={loading}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom commercial</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom générique</label>
              <input
                type="text"
                name="genericName"
                value={formData.genericName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Antibiotique">Antibiotique</option>
                <option value="Antalgique">Antalgique</option>
                <option value="Anti-inflammatoire">Anti-inflammatoire</option>
                <option value="Antihistaminique">Antihistaminique</option>
                <option value="Antiviral">Antiviral</option>
                <option value="Antifongique">Antifongique</option>
                <option value="Vitamines">Vitamines</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Forme galénique</label>
              <select
                name="dosageForm"
                value={formData.dosageForm}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Comprimé">Comprimé</option>
                <option value="Gélule">Gélule</option>
                <option value="Sirop">Sirop</option>
                <option value="Injection">Injection</option>
                <option value="Pommade">Pommade</option>
                <option value="Crème">Crème</option>
                <option value="Spray">Spray</option>
                <option value="Gouttes">Gouttes</option>
                <option value="Suppositoire">Suppositoire</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stock</label>
              <input
                type="number"
                name="stockQuantity"
                value={formData.stockQuantity}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prix (USD)</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="requiresPrescription"
              checked={formData.requiresPrescription}
              onChange={handleChange}
              className="rounded border-gray-300"
            />
            <label className="text-sm text-gray-700">Prescription requise</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center space-x-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicamentEditModal;

